import { Image } from '@chakra-ui/react';
import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { Box, Text, Heading, theme } from '@everlywell/ui-kit';
import HorizontalProductCardV2 from 'components/HorizontalProductCardV2';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import SectionHeader from 'components/PageSections/components/SectionHeader';
import PriceV2 from 'components/PricingV2/PriceV2';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import renderRichText, {
  ContentfulRichText,
} from 'utils/contentful/renderRichText';
import { ImageType } from 'utils/types';
import { ContentfulFlag } from 'utils/types/contentfulTypes';

import { HorizontalProductCardSubsection } from './BannerAndCardsV2.data';

export interface BannerAndCardsV2Props {
  eyebrow?: string;
  title?: string;
  bgColor?: string;
  flags?: ContentfulFlag[];
  headline?: ContentfulRichText;
  desktopBackgroundImage?: ImageType;
  desktopBackgroundImageAltText?: string;
  centerContent?: boolean;
  productsSubsection?: HorizontalProductCardSubsection;
}

const options: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <p
        style={{
          fontSize: theme.fontSizes['lg'],
          fontWeight: theme.fontWeights.normal,
          marginBottom: theme.space[3],
        }}
      >
        {children}
      </p>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Heading
        as="h2"
        fontSize={['xl', '5xl']}
        fontWeight="normal"
        lineHeight={['27px', '41.60px']}
        marginBottom="4"
        color="moonlight.base"
      >
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Heading
        as="h3"
        fontSize={['xl', '2xl']}
        fontWeight="medium"
        lineHeight={['27px', '28px']}
        marginBottom="8"
      >
        {children}
      </Heading>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <ul
        style={{
          fontSize: theme.fontSizes['lg'],
          listStyleType: 'initial',
          marginInlineStart: '1em',
        }}
      >
        <li style={{ listStyle: 'disc' }}>{children}</li>
      </ul>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <ol
        style={{
          fontSize: theme.fontSizes['lg'],
          listStyleType: 'initial',
          marginInlineStart: '1em',
        }}
      >
        <li style={{ listStyle: 'numeric' }}>{children}</li>
      </ol>
    ),
  },
};

/**
 * BannerAndCardsV2 is a component that displays a banner section with a background image
 * and content on the left side with text and HorizontalProductCard.
 */
const BannerAndCardsV2: React.FC<BannerAndCardsV2Props | null> = (props) => {
  if (!props) {
    return null;
  }

  const {
    eyebrow,
    title,
    bgColor,
    flags,
    headline,
    desktopBackgroundImage,
    desktopBackgroundImageAltText,
    productsSubsection,
  } = props;

  const hasBackgroundShape = flags?.map(
    (flag: ContentfulFlag) =>
      flag.displayName === 'hasBackgroundShape' && flag.value === true,
  );

  return (
    <SectionContainer size={['sm', 'sm', 'sm', 'xl']} centerContent>
      <SectionHeader title={title} eyebrow={eyebrow}></SectionHeader>
      <Box
        background={bgColor}
        display={['flex']}
        flexFlow={['column', 'row']}
        height={['auto', 'auto', '622px']}
        overflow="hidden"
        position={['unset', 'relative']}
        borderRadius={12}
      >
        <Box
          flex={['unset', 1]}
          padding={['6', '12']}
          order={['2', '1']}
          display="flex"
          flexFlow="column"
          alignItems="stretch"
          zIndex={['initial', '1']}
          justifyContent={'center'}
        >
          {headline ? (
            <Box width={['100%', '80%']} marginTop={['6', 'unset']}>
              {renderRichText(headline, options)}
            </Box>
          ) : null}
          {productsSubsection ? (
            <Box
              position={['unset', 'unset', 'absolute']}
              width={['100%', '100%', '65%']}
              zIndex={['unset', '1']}
              bottom={['unset', '48px']}
            >
              <Text
                fontSize="lg"
                fontWeight={theme.fontWeights.medium}
                marginBottom="3"
                marginTop={['8', 'unset']}
              >
                {productsSubsection.displayName}
              </Text>
              <Box
                display={['block', 'flex']}
                alignItems={['block', 'flex-start', 'flex']}
                justifyContent="space-between"
                gap="20px"
              >
                <Box
                  gap={['unset', '5']}
                  display={['block', 'block', 'flex']}
                  flex="1"
                >
                  {productsSubsection.products.map((product) => (
                    <HorizontalProductCardV2
                      key={product.displayName}
                      image={
                        product.image ? (
                          <GatsbyImage
                            image={product.image.gatsbyImageData}
                            alt={`${product?.displayName} box image`}
                            style={{
                              width: '120px',
                              height: 'auto',
                              zIndex: '1',
                            }}
                          ></GatsbyImage>
                        ) : null
                      }
                      pricing={
                        <>
                          <Text
                            fontSize={['sm', 'md']}
                            color={theme.colors.utility.sale}
                            marginRight="2"
                          >
                            {product.creditCost} Credit
                            {product.creditCost && product.creditCost > 1
                              ? 's'
                              : ''}
                          </Text>
                          <PriceV2
                            dollarAmount={product.dollarAmount.toString()}
                            centsAmount={product.centsAmount
                              .toFixed(1)
                              .replace('.', '')}
                            variant="strikethrough"
                          ></PriceV2>
                        </>
                      }
                    >
                      <Text
                        fontSize={['md', 'lg']}
                        color="tints.black"
                        fontWeight="medium"
                      >
                        {product.displayName}
                      </Text>
                    </HorizontalProductCardV2>
                  ))}
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box
          flex={['unset', 1]}
          display="flex"
          alignItems={['center', 'flex-start', 'center']}
          justifyContent="center"
          order={['1', '2']}
          height={['300px', 'auto']}
          overflow={['unset', 'hidden']}
          position="relative"
        >
          {desktopBackgroundImage ? (
            hasBackgroundShape ? (
              <Image
                src={
                  desktopBackgroundImage.gatsbyImageData.images.fallback?.src
                }
                alt={desktopBackgroundImageAltText || ''}
                boxSize="100%"
                objectFit="cover"
                clipPath={[
                  'polygon(1% 100%, 1% 0%, 100% 0%, 100% 100%, 87% 81%)',
                  'polygon(6% 0%, 100% 0%, 100% 100%, 0% 100%, 28% 40%)',
                ]}
              />
            ) : (
              <Image
                src={
                  desktopBackgroundImage.gatsbyImageData.images.fallback?.src
                }
                alt={desktopBackgroundImageAltText || ''}
              />
            )
          ) : null}
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default BannerAndCardsV2;
